/**
 * 整屏横向停靠。每屏个数按容器宽度当场量，只数能看全的。
 * 露出半个也只按整颗算：3 个半按 3 个翻，4 个半按 4 个翻。
 * 最后多出来的项只再滚到末尾，不再倒回凑满一屏。
 * 手势和惯性停稳之后才吸附。顺着滑动方向拉开约两成就翻页，不用拉过一半。
 * 不渲染小点，不克隆节点，不用逐项 CSS scroll-snap。
 *
 * pageStarts(7, 3) => [0, 3, 6]
 *
 * mountScreen 按首页博客评论块算出每一屏起点，只给这些卡片打吸附点。
 * 松手后的缓动交给浏览器的 scroll-snap，不再补第二次滚动。
 * 往右：screenPageStarts(10, 4) => [0, 4, 6]，对应 [1-4][5-8][7-10]。
 * 往左：screenPageStarts(10, 4, -1) => [0, 2, 6]，对应 [1-4][3-6][7-10]。
 */
;(function (root, factory) {
  const api = factory()
  if (typeof module === 'object' && module.exports) module.exports = api
  if (root && !root.pqPagedScroller) root.pqPagedScroller = api
})(typeof window !== 'undefined' ? window : null, function () {
  function pageSize(containerWidth, itemWidth, gap) {
    const width = Number(itemWidth) || 0
    const space = Number(gap) || 0
    if (width <= 0) return 1
    const size = Math.floor((Number(containerWidth) + space) / (width + space))
    return size < 1 ? 1 : size
  }

  function pageStarts(total, size) {
    const count = Math.max(0, Number(total) || 0)
    let perPage = Math.max(1, Number(size) || 1)
    if (count === 0) return []
    if (perPage > count) perPage = count
    if (count <= perPage) return [0]
    const starts = []
    for (let start = 0; start < count; start += perPage) starts.push(start)
    return starts
  }

  function screenPageStarts(total, size, direction) {
    const count = Math.max(0, Number(total) || 0)
    let perPage = Math.max(1, Number(size) || 1)
    if (count === 0) return []
    if (perPage > count) perPage = count
    if (count <= perPage) return [0]
    if (direction < 0) {
      const starts = [0]
      let start = count - perPage
      while (start > 0) {
        starts.push(start)
        start -= perPage
      }
      starts.sort((a, b) => a - b)
      return starts
    }
    const pageCount = Math.ceil(count / perPage)
    const starts = []
    for (let page = 0; page < pageCount; page += 1) {
      starts.push(Math.min(page * perPage, count - perPage))
    }
    return starts
  }

  function commitThreshold(span) {
    if (span <= 0) return 0
    return Math.min(Math.max(span * 0.18, 12), span * 0.45)
  }

  function destinationIndex(stops, origin, current) {
    const points = (stops || []).map((stop) => (typeof stop === 'number' ? stop : stop.left))
    if (!points.length) return 0
    let originIndex = 0
    for (let index = 0; index < points.length; index += 1) {
      if (points[index] <= origin + 1) originIndex = index
    }
    const delta = current - origin
    if (Math.abs(delta) <= 1) return originIndex
    if (delta > 0) {
      let index = originIndex
      for (let page = originIndex; page < points.length - 1; page += 1) {
        if (current >= points[page] + commitThreshold(points[page + 1] - points[page])) index = page + 1
        else break
      }
      return index
    }
    let index = originIndex
    for (let page = originIndex; page > 0; page -= 1) {
      if (current <= points[page] - commitThreshold(points[page] - points[page - 1])) index = page - 1
      else break
    }
    return index
  }

  function mount(el, options) {
    if (!el) return null
    const itemSelector = (options && options.itemSelector) || ':scope > *'
    let stops = []
    let pendingIndex = null
    let idleTimer = null
    let settling = false
    let touching = false
    let interacting = false
    let moved = false
    let endedWhileTouching = false
    let startX = 0
    let restLeft = 0
    let originLeft = 0
    let gestureArmed = false
    let observer = null
    const supportsScrollEnd = 'onscrollend' in el

    function items() {
      return Array.from(el.querySelectorAll(itemSelector)).filter((node) => node.parentElement === el)
    }

    function gap() {
      const styles = window.getComputedStyle(el)
      const value = parseFloat(styles.columnGap || styles.gap || '0')
      return Number.isNaN(value) ? 0 : value
    }

    function maxScroll() {
      return Math.max(0, el.scrollWidth - el.clientWidth)
    }

    function motion() {
      if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return 'auto'
      return 'smooth'
    }

    function measure() {
      const list = items()
      const total = list.length
      if (!total || !list[0].offsetWidth) {
        stops = []
        el.classList.add('is-static')
        return
      }
      const perPage = Math.min(pageSize(el.clientWidth, list[0].offsetWidth, gap()), total)
      const starts = pageStarts(total, perPage)
      const limit = maxScroll()
      const next = []
      starts.forEach((start) => {
        const node = list[start]
        if (!node) return
        const left = Math.min(
          Math.max(0, node.getBoundingClientRect().left - el.getBoundingClientRect().left + el.scrollLeft),
          limit
        )
        const previous = next[next.length - 1]
        if (!previous || Math.abs(previous.left - left) > 8) next.push({ start, left })
      })
      stops = next
      el.classList.toggle('is-static', stops.length < 2 || limit <= 1)
    }

    function nearestIndex() {
      const current = el.scrollLeft
      let index = 0
      let distance = Infinity
      stops.forEach((stop, stopIndex) => {
        const delta = Math.abs(stop.left - current)
        if (delta < distance) {
          distance = delta
          index = stopIndex
        }
      })
      return index
    }

    function clearIdle() {
      window.clearTimeout(idleTimer)
      idleTimer = null
    }

    function finishInteraction() {
      if (touching || pendingIndex != null || idleTimer != null) return
      interacting = false
      endedWhileTouching = false
      gestureArmed = false
      restLeft = el.scrollLeft
      originLeft = el.scrollLeft
    }

    function armGesture() {
      if (gestureArmed) return
      gestureArmed = true
      originLeft = restLeft
    }

    function scheduleSettle() {
      if (touching || pendingIndex != null) return
      clearIdle()
      idleTimer = window.setTimeout(() => {
        idleTimer = null
        settle()
      }, 120)
    }

    function scrollToStop(index, behavior) {
      const stop = stops[index]
      if (!stop) return
      clearIdle()
      if (Math.abs(el.scrollLeft - stop.left) <= 1) {
        pendingIndex = null
        finishInteraction()
        return
      }
      pendingIndex = index
      el.scrollTo({ left: stop.left, behavior: behavior || motion() })
    }

    function settle() {
      clearIdle()
      if (touching || settling) return
      settling = true
      try {
        if (stops.length < 2) {
          pendingIndex = null
          finishInteraction()
          return
        }
        if (pendingIndex != null) {
          const stop = stops[pendingIndex]
          if (stop && Math.abs(el.scrollLeft - stop.left) > 2) {
            el.scrollTo({ left: stop.left, behavior: 'auto' })
            return
          }
          pendingIndex = null
          finishInteraction()
          return
        }
        const index = destinationIndex(stops, originLeft, el.scrollLeft)
        const stop = stops[index]
        if (!stop || Math.abs(el.scrollLeft - stop.left) <= 2) {
          finishInteraction()
          return
        }
        scrollToStop(index, motion())
      } finally {
        settling = false
      }
    }

    function onScroll() {
      if (pendingIndex != null) {
        const stop = stops[pendingIndex]
        if (stop && Math.abs(el.scrollLeft - stop.left) <= 1) {
          pendingIndex = null
          finishInteraction()
        }
        return
      }
      const nearest = stops[nearestIndex()]
      if (!touching && !interacting && nearest && Math.abs(el.scrollLeft - nearest.left) <= 2) {
        finishInteraction()
        return
      }
      interacting = true
      endedWhileTouching = false
      if (!touching) armGesture()
      if (touching) return
      if (supportsScrollEnd) {
        clearIdle()
        return
      }
      scheduleSettle()
    }

    function onScrollEnd() {
      if (settling) return
      if (pendingIndex != null) {
        const stop = stops[pendingIndex]
        if (stop && Math.abs(el.scrollLeft - stop.left) <= 1) pendingIndex = null
      }
      if (touching) {
        endedWhileTouching = true
        return
      }
      settle()
    }

    function onPointerDown(event) {
      if (event.pointerType === 'mouse' && event.button !== 0) return
      touching = true
      interacting = true
      moved = false
      endedWhileTouching = false
      startX = event.clientX
      originLeft = el.scrollLeft
      gestureArmed = true
      clearIdle()
      pendingIndex = null
      const link = event.target && event.target.closest ? event.target.closest('a') : null
      if (link && el.contains(link) && event.pointerType === 'mouse') event.preventDefault()
      window.requestAnimationFrame(() => {
        const active = document.activeElement
        if (!touching || !active || active === document.body || !el.contains(active)) return
        if (typeof active.blur === 'function') active.blur()
      })
    }

    function onPointerMove(event) {
      if (!touching) return
      if (Math.abs(event.clientX - startX) > 8) moved = true
    }

    function onPointerUp() {
      if (!touching) return
      touching = false
      if (!supportsScrollEnd) {
        scheduleSettle()
        return
      }
      if (endedWhileTouching) {
        settle()
        return
      }
      clearIdle()
      idleTimer = window.setTimeout(() => {
        idleTimer = null
        if (touching || pendingIndex != null) {
          finishInteraction()
          return
        }
        settle()
      }, 140)
    }

    function onClick(event) {
      if (!moved) return
      event.preventDefault()
      event.stopPropagation()
      moved = false
    }

    function onFocusIn(event) {
      if (touching || interacting) return
      const item = event.target && event.target.closest ? event.target.closest(itemSelector) : null
      if (!item || item.parentElement !== el) return
      scrollToItem(item, 'auto')
    }

    function scrollToItem(node, behavior) {
      const list = items()
      const index = list.indexOf(node)
      if (index < 0) return
      let page = 0
      stops.forEach((stop, stopIndex) => {
        if (stop.start <= index) page = stopIndex
      })
      scrollToStop(page, behavior || motion())
    }

    el.addEventListener('scroll', onScroll, { passive: true })
    if (supportsScrollEnd) el.addEventListener('scrollend', onScrollEnd)
    el.addEventListener('pointerdown', onPointerDown)
    el.addEventListener('pointermove', onPointerMove)
    el.addEventListener('pointerup', onPointerUp)
    el.addEventListener('pointercancel', onPointerUp)
    window.addEventListener('pointerup', onPointerUp)
    window.addEventListener('pointercancel', onPointerUp)
    el.addEventListener('click', onClick, true)
    el.addEventListener('focusin', onFocusIn)

    if (window.ResizeObserver) {
      observer = new ResizeObserver(measure)
      observer.observe(el)
    } else {
      window.addEventListener('resize', measure)
    }
    measure()

    return {
      refresh: measure,
      scrollToItem,
      isTouching: () => touching,
      isInteracting: () => touching || interacting || pendingIndex != null || idleTimer != null,
      destroy() {
        clearIdle()
        el.removeEventListener('scroll', onScroll)
        if (supportsScrollEnd) el.removeEventListener('scrollend', onScrollEnd)
        el.removeEventListener('pointerdown', onPointerDown)
        el.removeEventListener('pointermove', onPointerMove)
        el.removeEventListener('pointerup', onPointerUp)
        el.removeEventListener('pointercancel', onPointerUp)
        window.removeEventListener('pointerup', onPointerUp)
        window.removeEventListener('pointercancel', onPointerUp)
        el.removeEventListener('click', onClick, true)
        el.removeEventListener('focusin', onFocusIn)
        if (observer) observer.disconnect()
        else window.removeEventListener('resize', measure)
      },
    }
  }

  function mountScreen(el, options) {
    if (!el) return null
    const itemSelector = (options && options.itemSelector) || ':scope > *'
    let touching = false
    let dragged = false
    let dragX = 0
    let anchorLeft = 0
    let direction = 1
    let observer = null

    function items() {
      return Array.from(el.querySelectorAll(itemSelector)).filter((node) => {
        return node.parentElement === el && !node.hidden
      })
    }

    function gap() {
      const styles = window.getComputedStyle(el)
      const value = parseFloat(styles.columnGap || styles.gap || '0')
      return Number.isNaN(value) ? 0 : value
    }

    function isRow() {
      const styles = window.getComputedStyle(el)
      return styles.display === 'flex' && styles.flexWrap === 'nowrap' && styles.overflowX !== 'visible'
    }

    function measure() {
      const list = items()
      list.forEach((node) => node.classList.remove('is-snap'))
      if (!isRow() || list.length < 2 || !list[0].offsetWidth) return
      const perPage = Math.min(pageSize(el.clientWidth, list[0].offsetWidth, gap()), list.length)
      const seen = new Set()
      screenPageStarts(list.length, perPage, direction).forEach((start) => {
        if (seen.has(start) || !list[start]) return
        seen.add(start)
        list[start].classList.add('is-snap')
      })
    }

    function setDirection(next) {
      if (next === direction) return
      direction = next
      measure()
    }

    function onWheel(event) {
      if (Math.abs(event.deltaX) < Math.abs(event.deltaY) || !event.deltaX) return
      setDirection(event.deltaX > 0 ? 1 : -1)
    }

    function onScroll() {
      if (!touching) return
      const delta = el.scrollLeft - anchorLeft
      if (Math.abs(delta) < 4) return
      setDirection(delta > 0 ? 1 : -1)
    }

    function onPointerDown(event) {
      if (event.pointerType === 'mouse' && event.button !== 0) return
      touching = true
      dragged = false
      dragX = event.clientX
      anchorLeft = el.scrollLeft
    }

    function onPointerMove(event) {
      if (!touching || Math.abs(event.clientX - dragX) <= 8) return
      dragged = true
    }

    function onPointerUp() {
      touching = false
    }

    function onClick(event) {
      if (!dragged) return
      event.preventDefault()
      event.stopPropagation()
      dragged = false
    }

    el.addEventListener('wheel', onWheel, { passive: true })
    el.addEventListener('scroll', onScroll, { passive: true })
    el.addEventListener('pointerdown', onPointerDown)
    el.addEventListener('pointermove', onPointerMove)
    el.addEventListener('pointerup', onPointerUp)
    el.addEventListener('pointercancel', onPointerUp)
    el.addEventListener('click', onClick, true)

    if (window.ResizeObserver) {
      observer = new ResizeObserver(measure)
      observer.observe(el)
    } else {
      window.addEventListener('resize', measure)
    }
    measure()

    return {
      refresh: measure,
      destroy() {
        items().forEach((node) => node.classList.remove('is-snap'))
        el.removeEventListener('wheel', onWheel)
        el.removeEventListener('scroll', onScroll)
        el.removeEventListener('pointerdown', onPointerDown)
        el.removeEventListener('pointermove', onPointerMove)
        el.removeEventListener('pointerup', onPointerUp)
        el.removeEventListener('pointercancel', onPointerUp)
        el.removeEventListener('click', onClick, true)
        if (observer) observer.disconnect()
        else window.removeEventListener('resize', measure)
      },
    }
  }

  return { pageSize, pageStarts, screenPageStarts, destinationIndex, mount, mountScreen }
})
